import { useMemo } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import useLocalization from '../../../hooks/useLocalization';
import { FormTextField } from '../../molecules/form-elements';
import StyledForm from '../shared/StyledForm';
import { usePay2Local } from '../../../hooks/payment/usePay2Local';
import { PAYMENT_TRANSACTION_TYPE } from '../../../types/enums';

const styles = {
  button: {
    width: '30%',
    color: 'neutral.300',
    border: '1px solid',
    borderColor: 'neutral.300',
    borderRadius: 2,
    height: 40,
    textTransform: 'uppercase',
    '&.MuiToggleButtonGroup-grouped': {
      borderRadius: 2,
      '&:not(:first-of-type):not(.Mui-selected)': {
        borderLeft: '1px solid',
        borderColor: 'neutral.300',
      },
    },
    '&.Mui-selected': {
      backgroundColor: 'primary.main',
      color: 'primary.contrastText',
      border: 'none',
      '&:hover': {
        backgroundColor: 'primary.main',
      },
    },
  },
  toggleButtonGroup: {
    justifyContent: 'space-between',
    width: '100%',
  },
};

const PREDEFINED_INPUTS = [400, 600, 800];

const Pay2LocalDepositForm = ({ paymentProvider }: { paymentProvider: string | null }) => {
  const { t } = useLocalization();
  const {
    mutation: { mutate: deposit, isPending },
  } = usePay2Local(PAYMENT_TRANSACTION_TYPE.DEPOSIT);

  const schema = useMemo(() => {
    return z.object({
      amount: z.coerce
        .number({ error: t('invalidNumber') })
        .min(200, t('minValue', { minAmount: 200 }))
        .max(100000, t('maxValue', { maxAmount: 100000 })),
      currency: z.string(),
      bank_type: z.string().nullable(),
    });
  }, [t]);

  type FormInput = z.input<typeof schema>;
  type FormData = z.output<typeof schema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInput, unknown, FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: 400,
      currency: 'BDT',
      bank_type: paymentProvider,
    },
  });

  const handleFormSubmit: SubmitHandler<FormData> = (data) => {
    deposit({
      ...data,
      redirect_url: window.location.origin,
    });
  };

  return (
    <StyledForm
      submitTitle={t('continue')}
      onSubmit={handleSubmit(handleFormSubmit)}
      isSubmitting={isPending}
      disablePadding
    >
      <FormTextField
        name='amount'
        control={control}
        label={t('sum')}
        variant='outlined'
        fullWidth
        type='number'
        error={!!errors.amount}
        helperText={errors.amount?.message}
        disabled={isPending}
        required
        inputMode='numeric'
      />
      <Controller
        name='amount'
        control={control}
        render={({ field }) => (
          <ToggleButtonGroup
            exclusive
            {...field}
            onChange={(_event, newValue) => {
              if (newValue !== null) {
                field.onChange(newValue);
              }
            }}
            sx={styles.toggleButtonGroup}
          >
            {PREDEFINED_INPUTS.map((value) => (
              <ToggleButton selected={field.value === value} key={value} value={value} sx={styles.button}>
                {value}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      />
    </StyledForm>
  );
};

export default Pay2LocalDepositForm;
